import { Component, Inject, OnInit } from '@angular/core';
import { ModalData, ModalRef } from '@purespectrum1/ui/modal';
import { ToasterService } from '@purespectrum1/ui/toaster-service';
import { notifyMessage } from '../../../shared/constants/notify-message';
import { SURVEY_STATUS } from '../../../shared/constants/survey-status';
import {
  ManualFixModal,
  ReconcilationModal,
  ReconcilationSummaryModal,
  SurveyListStatus,
} from '../../interfaces/adjustments-reconciliation-service.interface';
import { AdjustmentsReconciliationService } from '../../services/adjustments-reconciliation/adjustments-reconciliation-service.service';
import { Constants } from '../manage-reconcile-constants';
import { LiveReconciliationStatus } from './live-reconciliation-status';
import { AuthService } from '../../../shared/services/auth/auth.service';

@Component({
  selector: 'ps-ui-adjustment-modal',
  templateUrl: './adjustment-modal.component.html',
  styleUrls: ['./adjustment-modal.component.css'],
})
export class AdjustmentModalComponent implements OnInit {
  reconSummaryData: ReconcilationModal;
  mfSummaryData?: ManualFixModal;
  liveOption = Constants.LIVE_RECO_STATUS_OPTIONS;
  public recoModalSurveyList!: string;
  public invoicedSurveyList = [];
  public mfModalSurveyList!: string;
  public isLiveRecon: boolean = false;
  private readonly _liveReconciliationStatus = new LiveReconciliationStatus();
  public desiredStatusLabel: string =
    this._liveReconciliationStatus.labelStatusByCode(
      Constants.LIVE_RECO_STATUS_OPTIONS.COMPLETES
    );
  public liveUpdatedTransactions = 0;
  public isConfirmationTitle = false;
  public livePausedSurveyList: string = '';
  public ineligibleAndReconciled: number = 0;
  public isMultiSurvey: boolean = false;
  public allSurveysRejectPercentageFine: boolean = false;
  public reconciliationRejectionPercentageLimit: number = 0;
  constructor(
    @Inject(ModalData) private _modalData: any,
    private _modalRef: ModalRef<AdjustmentModalComponent, string>,
    private _adjustmentReconciliationService: AdjustmentsReconciliationService,
    private _toastr: ToasterService,
    private _auth: AuthService
  ) {}

  close(msg?: string) {
    this._modalRef.close(msg);
  }
  ngOnInit() {
    this._setUpModal();
    this.isConfirmationTitle = this._setIsConfirmationTitle();
  }
  confirmAdjustment(adjustmentId: string) {
    if (!this.allSurveysRejectPercentageFine) {
      return this._modalRef.close();
    }
    this._adjustmentReconciliationService
      .confirmAdjustment(adjustmentId)
      .subscribe((response) => {
        this._modalRef.close();
        this._toastr.success('File uploaded successfully. Preparing a summary');
      });
  }
  declineAdjustment(adjustmentId: string) {
    this._adjustmentReconciliationService
      .declinedAdjustment(adjustmentId)
      .subscribe((response) => {
        this._modalRef.close();
        this._toastr.success('Adjustment declined successfully.');
      });
  }

  private _setUpModal(): void {
    let { type, id, desiredStatus, invoicedSurveys = [] } = this._modalData;
    id = id?.filter((surveyId: number) => !invoicedSurveys.includes(surveyId));
    const { MANUAL_FIX, CLOSED, LIVE } = Constants.OPERATION_TYPES;
    const surveyList = id?.toString().replace(/,/g, ', ');

    if (type === MANUAL_FIX) {
      this.mfModalSurveyList = surveyList;
      this.mfSummaryData = this._modalData;
    }
    if ([CLOSED, LIVE].includes(type)) {
      if (type === LIVE) {
        this.isLiveRecon = true;
        this.desiredStatusLabel =
          this._liveReconciliationStatus.labelStatusByCode(desiredStatus);
      }
      this.recoModalSurveyList = surveyList;
      this.invoicedSurveyList = invoicedSurveys;
      this.reconSummaryData = this._modalData;
      this.reconciliationRejectionPercentageLimit =
        this.reconSummaryData.reconciliationRejectionPercentageLimit;
      this.isMultiSurvey =
        this.reconSummaryData.summary.summaryPerSurvey.length > 1;
      this.allSurveysRejectPercentageFine =
        this._allSurveysWithValidRejectPercentages(
          this.reconSummaryData.summary
        );
      this.livePausedSurveyList = this._getLivePausedSurveyList(
        this.reconSummaryData.summary.surveyStatus
      )
        ?.toString()
        .replace(/,/g, ', ');
      this.ineligibleAndReconciled = this._getIneligibleAndReconciled();
      this._setSummaryDetail();
    } else {
      this._toastr.error(notifyMessage.errorMessage.OPERATION_UNAVAILABLE);
      this.close();
    }
  }

  private _allSurveysWithValidRejectPercentages(
    summary: ReconcilationSummaryModal
  ): boolean {
    if (this._auth.userType === 'operator') {
      return true;
    }
    for (let index = 0; index < summary.summaryPerSurvey.length; index++) {
      const surveySummary = summary.summaryPerSurvey[index];
      if (!surveySummary.validCap) {
        return false;
      }
    }
    return true;
  }

  private _setSummaryDetail(): void {
    this.liveUpdatedTransactions =
      this.reconSummaryData.summary.eligible -
      this.reconSummaryData.summary.transactionsWithDesiredStatus;
  }

  private _setIsConfirmationTitle() {
    return !!(
      this.reconSummaryData &&
      (this.reconSummaryData.desiredStatus ||
        this.reconSummaryData.desiredStatus === 0)
    );
  }

  private _getLivePausedSurveyList(surveyListStatus: SurveyListStatus[]) {
    return surveyListStatus
      ?.filter((survey: SurveyListStatus) =>
        [SURVEY_STATUS.LIVE, SURVEY_STATUS.PAUSED].includes(survey.st)
      )
      .map((survey) => survey.id);
  }

  private _getIneligibleAndReconciled() {
    return (
      this.reconSummaryData.summary.transactions -
      this.reconSummaryData.summary.eligible
    );
  }
}
